import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { AlertService } from './alert.service';
import { EditUserPayload, User, UserCreatePayload, UserListQuery } from 'src/interfaces/user';
import { ConfirmationService } from './confirmation.service';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  baseUrl: string = environment.api.baseUrl;
  headers: HttpHeaders;
  totalNumberOfUsers: number = 0;
  private _paginationToken: string | null = null;

  constructor(
    private _http: HttpClient,
    private _alertService: AlertService,
    private _auth: AuthService,
    private _confirmation: ConfirmationService,
    private _utilsService: UtilsService
  ) {
    this._auth.generateRequestHeaders().then((headers) => {
      this.headers = headers;
    });
  }

  async createUser(payload: UserCreatePayload): Promise<boolean> {
    const confirm = await this._confirmation.confirm(
      'Create user',
      'Confirm that you want to create a new user'
    );

    if (!confirm) return false;

    try {
      const { success, message } = await this._http
        .post<any>(`${this.baseUrl}/user`, payload, {
          headers: this.headers,
        })
        .toPromise();

      this._alertService.openSnackBar(message);

      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  async getUser(username: string): Promise<User | null> {
    try {
      const { data } = await this._http
        .get<any>(`${this.baseUrl}/user/${username}`, {
          headers: this.headers,
        })
        .toPromise();

      const user = {
        ...data,
        Attributes: data.UserAttributes || data.Attributes || [],
      };
      const attrs = (user.Attributes || []) as Array<{ Name: string; Value: string }>;
      const toNum = (v: unknown): number | undefined => {
        if (v == null) return undefined;
        if (typeof v === 'number' && !Number.isNaN(v)) return v;
        if (typeof v === 'string') {
          const n = parseInt(v, 10);
          return Number.isNaN(n) ? undefined : n;
        }
        return undefined;
      };
      const idFromAttr = (...names: string[]): number | undefined => {
        for (const name of names) {
          const a = attrs.find((x) => x.Name === name);
          if (a?.Value != null && a.Value !== '') return toNum(a.Value);
        }
        return undefined;
      };
      // Support nested data.user (some APIs return user under data.user)
      const top = (data as any).user != null ? (data as any).user : data;
      // Company: top-level or nested or Cognito-style custom attributes
      const companyId = toNum(data.company_id ?? top.company_id) ?? idFromAttr('custom:company_id', 'company_id');
      if (companyId != null) user.company_id = companyId;
      if (data.company != null || top.company != null) user.company = data.company ?? top.company;
      // Individual: top-level or nested or Cognito-style custom attributes
      const individualId = toNum(data.individual_id ?? (data as any).individualId ?? top.individual_id) ?? idFromAttr('custom:individual_id', 'individual_id');
      if (individualId != null) user.individual_id = individualId;
      if (data.individual != null || top.individual != null) user.individual = data.individual ?? top.individual;

      return user;
    } catch (error) {
      this._utilsService.showError(error);
      return null;
    }
  }

  async editUser(username: string, payload: EditUserPayload): Promise<boolean> {
    const confirm = await this._confirmation.confirm(
      'Update user',
      'Confirm to update user'
    );
    if (!confirm) return false;

    try {
      const { data, success, message } = await this._http
        .put<any>(`${this.baseUrl}/user/${username}`, payload, {
          headers: this.headers,
        })
        .toPromise();

      this._alertService.openSnackBar(message);

      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  /**
   * User list: GET /user/list with optional search, search_practice, enabled, token.
   * Omit `enabled` when query.enabled is default (enabled users only).
   */
  async getUsers(
    limit: number,
    token: string | null = null,
    query: UserListQuery = {}
  ): Promise<User[]> {
    let queryString = `limit=${limit}`;

    if (token !== null && token !== '') {
      queryString += `&token=${encodeURIComponent(token)}`;
    }

    const search = (query.search ?? '').trim();
    if (search !== '') {
      queryString += `&search=${encodeURIComponent(search)}`;
    }

    const practice = (query.searchPractice ?? '').trim();
    if (practice !== '') {
      queryString += `&search_practice=${encodeURIComponent(practice)}`;
    }

    if (query.enabled === 'all') {
      queryString += `&enabled=all`;
    } else if (query.enabled === 'disabled') {
      queryString += `&enabled=0`;
    }

    try {
      const response = await this._http
        .get<any>(`${this.baseUrl}/user?${queryString}`, {
          headers: this.headers,
        })
        .toPromise();
  
      const { data } = response;
      
      this._paginationToken = data.PaginationToken ? data.PaginationToken : null;
  
      return data.Users;
    } catch (error) {
      this._utilsService.showError(error);
      return [];
    }
  }
  
  getTotalNumberOfUsers() {
    return this.totalNumberOfUsers;
  }

  getPaginationToken(): string | null {
    return this._paginationToken;
  }

  async enableUser(Username: string) {
    const confirm = await this._confirmation.confirm(
      'Enable user',
      'Confirm to enable user'
    );
    if (!confirm) return false;

    try {
      await this._http
        .get(`${this.baseUrl}/user/${Username}/enable`, {
          headers: this.headers,
        })
        .toPromise();

      this._alertService.openSnackBar('User was successfully enabled');
      return true;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  getFormData(formData: string[]) {
    return this._http
      .post<any>(`${this.baseUrl}/form-data`, formData, {
        headers: this.headers,
      })
      .pipe(
        map((response) => response.data),
        catchError((error: HttpErrorResponse) => {
          this._utilsService.showError(error);
          return throwError(error);
        })
      );
  }

  async disableUser(name: string): Promise<boolean> {
    const confirm = await this._confirmation.confirm(
      'Disable user',
      'Confirm to disable user'
    );
    if (!confirm) return false;

    try {
      const { success, message } = await this._http
        .delete<any>(`${this.baseUrl}/user/${name}`, {
          headers: this.headers,
        })
        .toPromise();

      this._alertService.openSnackBar(message);

      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  async resetPassword(name: string): Promise<boolean> {
    
    const confirm = await this._confirmation.confirm(
      'Reset password for user',
      'Confirm to reset password for user'
    );

    if (!confirm) return false;

    try {
      const { success, message, data } = await this._http
        .get<any>(`${this.baseUrl}/user/${name}/reset-password`)
        .toPromise();
      this._alertService.openSnackBar(message);
      return data.password;
    } catch (error) {
        this._utilsService.showError(error);
        return false;
    }
  }
}
